import { trackGA4ViewCart, trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { getProductIdFromGlobalId, getVariantIdFromVariantURI } from '@ifixit/helpers';
import { SentryError } from '@ifixit/sentry';
import { CART_TOASTER_ID, CartToasterProvider, useCart } from '@ifixit/shopify-cart-sdk/hooks';
import { Toaster } from '@ifixit/ui/components/toast';
import { useDisclosure } from '@ifixit/ui/hooks/use-disclosure';
import { useMounted } from '@ifixit/ui/hooks/use-mounted';
import { createContext, useMemo, useContext, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { PropsWithChildren } from 'react';

export type CartDrawerContext = ReturnType<typeof useDisclosure>;
const CartDrawerContext = createContext<CartDrawerContext | null>(null);

type CartDrawerProviderProps = PropsWithChildren<{
   // Apps that already mount a Toaster on every page pass false: cart toasts
   // then go to that Toaster instead of a second stack pinned to the same
   // corner, where the two would overlap.
   mountToaster?: boolean;
}>;

export function CartDrawerProvider({ children, mountToaster = true }: CartDrawerProviderProps) {
   const disclosure = useDisclosure();
   // Sonner's Toaster renders differently on the server, so defer it to the
   // client to avoid hydration errors (same guard as the page-frame Toaster).
   const isMounted = useMounted();

   const value = useMemo((): CartDrawerContext => disclosure, [disclosure.isOpen]);

   return (
      <CartToasterProvider value={mountToaster ? CART_TOASTER_ID : undefined}>
         <CartDrawerContext.Provider value={value}>
            {children}
            {/* Sonner's outer element stays in normal flow even with no toasts
                up, and the carpenter header mounts this provider inside a flex
                row with a gap, so portal it out of the layout. */}
            {mountToaster &&
               isMounted &&
               createPortal(
                  <Toaster
                     id={CART_TOASTER_ID}
                     position="bottom-right"
                     mobileOffset={{ bottom: 'max(16px, env(safe-area-inset-bottom))' }}
                  />,
                  document.body
               )}
         </CartDrawerContext.Provider>
      </CartToasterProvider>
   );
}

export function useCartDrawer() {
   const context = useContext(CartDrawerContext);
   if (context === null) {
      throw new SentryError('useCartDrawer must be used within a CartDrawerProvider');
   }
   const onViewCart = useCallback(() => {
      trackAnalyticsCustomEvent({
         eventCategory: 'Mini Cart',
         eventAction: 'Btn "View Cart" - Click',
         eventName: `${window.location.origin}${window.location.pathname}`,
      });
      context.onClose();
   }, [context.onClose]);

   const cartQuery = useCart();
   // we only want to track the analytics when the cart drawer is opened by the user
   const onOpenWithAnalytics = useCallback(
      (event?: Event, autoOpened?: boolean) => {
         // Make sure the cart data is loaded and successful
         if (
            cartQuery.isSuccess &&
            cartQuery.data &&
            !autoOpened &&
            cartQuery.data.lineItems.length
         ) {
            trackGA4ViewCart({
               currency: cartQuery.data.lineItems[0].price.currencyCode,
               value: cartQuery.data.lineItems.reduce(
                  (acc: number, item) => acc + Number(item.price.amount) * item.quantity,
                  0
               ),
               items: cartQuery.data.lineItems.map(item => ({
                  item_id: item.itemcode,
                  item_name: item.name + ' ' + item.variantTitle,
                  item_variant: getVariantIdFromVariantURI(item.shopifyVariantId),
                  numericProductId: getProductIdFromGlobalId(item.shopifyProductId),
                  price: Number(item.price.amount),
                  quantity: item.quantity,
               })),
               shopifyCartId: cartQuery.data.shopifyCartId,
            });
         }
         context.onOpen();
      },
      [context.onOpen, cartQuery?.isSuccess, cartQuery?.data]
   );

   return { ...context, onOpen: onOpenWithAnalytics, onViewCart };
}
